import * as React from "react"
import { Link, useLocation } from "react-router-dom"
import { styled } from "@mui/material/styles"
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp"
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion"
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from "@mui/material/AccordionSummary"
import MuiAccordionDetails from "@mui/material/AccordionDetails"
import { Box, Typography } from "@mui/material"
import { useAppSelector } from "@/lib/hooks"
import { isAdminPortal } from "@/utilities/domain"

interface LeftSidebarMenu {
  toggleActive: () => void
}

// Custom styled Accordion components
const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&::before": {
    display: "none",
  },
}))

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#3a4252" : "#f6f7f9",
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
}))

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
}))

// Define the types for menu items
interface LinkItem {
  path: string
  title: string
  icon: string
  permission?: string
}

interface AccordionItem {
  panel: string
  title: string
  icon: string
  badge?: string
  links: {
    path: string
    label: string
    badge?: string
    badgeStyle?: string
    permission?: string
  }[]
}

type MenuItem = { section: string; items: (LinkItem | AccordionItem)[] }

const allMenuItems: MenuItem[] = [
  {
    section: "MAIN",
    items: [
      {
        path: "/pos/dashboard",
        title: "Dashboard",
        icon: "dashboard",
        permission: "dashboard-dashboard-view",
      },
      {
        path: "/pos/sales",
        title: "Sales",
        icon: "point_of_sale",
        permission: "sales-orders-view",
      },
      {
        path: "/pos/purchase",
        title: "Purchase",
        icon: "shopping_cart_checkout",
        permission: "inventory-purchases-view",
      },
    ],
  },
  {
    section: "Operations",
    items: [
      {
        panel: "sales-orders",
        title: "Sales & Orders",
        icon: "receipt",
        links: [
          {
            path: "/pos/website-orders",
            label: "Website Orders",
            permission: "sales-orders-view",
          },
          {
            path: "/pos/sales-return",
            label: "Sales Return",
            permission: "sales-sales_returns-view",
          },
          {
            path: "/pos/return-requests",
            label: "Return Requests",
            permission: "sales-sales_returns-view",
          },
          {
            path: "/pos/discount",
            label: "Discounts",
            permission: "sales-discounts-view",
          },
          {
            path: "/pos/footfall",
            label: "Footfall",
            permission: "sales-customer_returns-view",
          },
          {
            path: "/pos/cash-denominations",
            label: "Cash Denomination",
            permission: "sales-cash_denominations-view",
          },
          {
            path: "/pos/expenses",
            label: "Expenses",
            permission: "sales-expenses-view",
          },
        ],
      },
      {
        panel: "inventory",
        title: "Inventory",
        icon: "inventory_2",
        links: [
          {
            path: "/pos/products",
            label: "Products",
            permission: "inventory-products-view",
          },
          {
            path: "/pos/branch-products",
            label: "Branch Products",
            permission: "inventory-products-view",
          },
          {
            path: "/pos/product-variants",
            label: "Product Variants",
            permission: "inventory-product_variants-view",
          },
          {
            path: "/pos/categories",
            label: "Categories",
            permission: "inventory-categories-view",
          },
          {
            path: "/pos/attributes",
            label: "Attributes",
            permission: "inventory-attributes-view",
          },
          {
            path: "/pos/tags",
            label: "Tags",
            permission: "inventory-tags-view",
          },
          {
            path: "/pos/brands",
            label: "Brands",
            permission: "inventory-brands-view",
          },
          {
            path: "/pos/stock-adjustment",
            label: "Stock Adjustment",
            permission: "inventory-stock_adjustments-view",
          },
          {
            path: "/pos/stock-transactions",
            label: "Stock Transaction",
            permission: "inventory-stock_transactions-view",
          },
          {
            path: "/pos/stock-audit",
            label: "Stock Audit",
            permission: "inventory-stock_audits-view",
          },
          {
            path: "/pos/damage-products",
            label: "Damage Products",
            permission: "inventory-damage_products-view",
          },
        ],
      },
      {
        panel: "business",
        title: "Business",
        icon: "storefront",
        links: [
          {
            path: "/pos/customers",
            label: "Customers",
            permission: "business-customers-view",
          },
          {
            path: "/pos/suppliers",
            label: "Suppliers",
            permission: "business-suppliers-view",
          },
          {
            path: "/pos/locations",
            label: "Locations",
            permission: "business-locations-view",
          },
          {
            path: "/pos/delivery-fees",
            label: "Delivery Fees",
            permission: "business-delivery_fees-view",
          },
        ],
      },
      {
        panel: "report",
        title: "Reports",
        icon: "insights",
        links: [
          {
            path: "/pos/sales-report",
            label: "Sales Report",
            permission: "operations-reports-view",
          },
          {
            path: "/pos/products-report",
            label: "Product Report",
            permission: "operations-reports-view",
          },
          {
            path: "/pos/exports",
            label: "Exports",
            permission: "operations-exports-view",
          },
        ],
      },
    ],
  },
  {
    section: "Administration",
    items: [
      {
        panel: "hr",
        title: "HR",
        icon: "badge",
        links: [
          {
            path: "/pos/staff",
            label: "Staff",
            permission: "hr-staff_members-view",
          },
          {
            path: "/pos/attendance",
            label: "Attendance",
            permission: "hr-attendance-view",
          },
          {
            path: "/pos/incentives",
            label: "Incentives",
            permission: "hr-incentives-view",
          },
        ],
      },
      {
        panel: "user-management",
        title: "User Management",
        icon: "manage_accounts",
        links: [
          {
            path: "/pos/users",
            label: "Users",
            permission: "user_management-users-view",
          },
          {
            path: "/pos/roles",
            label: "Roles & Permissions",
            permission: "user_management-roles-view",
          },
        ],
      },
      {
        panel: "configuration",
        title: "Configuration",
        icon: "settings",
        links: [
          {
            path: "/pos/payment-methods",
            label: "Payment Methods",
            permission: "configuration-settings-view",
          },
          {
            path: "/pos/sales-status",
            label: "Sales Status",
            permission: "configuration-settings-view",
          },
          {
            path: "/pos/payment-status",
            label: "Payment Status",
            permission: "configuration-settings-view",
          },
          // { path: "/pos/reasons", label: "Reasons" },
          {
            path: "/pos/attribute-names",
            label: "Attributes",
            permission: "configuration-settings-view",
          },
          {
            path: "/pos/inventory-configs",
            label: "Inventory",
            permission: "configuration-settings-view",
          },
          {
            path: "/pos/currency-config",
            label: "Currency",
            permission: "configuration-settings-view",
          },
          {
            path: "/pos/note-config",
            label: "Currency Note",
            permission: "configuration-settings-view",
          },
          {
            path: "/pos/pos-config",
            label: "POS",
            permission: "configuration-settings-view",
          },
          {
            path: "/pos/website-config",
            label: "Website",
            permission: "configuration-settings-view",
          },
        ],
      },
    ],
  },
]

/**
 * The permission a branch-portal page needs, taken from its sidebar entry.
 * Pages without a sidebar entry (details, invoices) return undefined and are
 * left to the API to protect.
 */
export const requiredPermissionForPath = (
  pathname: string
): string | undefined => {
  for (const section of allMenuItems) {
    for (const item of section.items) {
      const entries = "links" in item ? item.links : [item]
      const match = entries.find(
        (entry) =>
          pathname === entry.path || pathname.startsWith(`${entry.path}/`)
      )
      if (match) {
        return match.permission
      }
    }
  }

  return undefined
}

const adminMenuItems: MenuItem[] = [
  {
    section: "MAIN",
    items: [
      { path: "/pos/dashboard", title: "Dashboard", icon: "dashboard" },
      { path: "/pos/sales", title: "Sales", icon: "point_of_sale" },
      {
        path: "/pos/purchase",
        title: "Purchase",
        icon: "shopping_cart_checkout",
      },
      { path: "/pos/branches", title: "Branches", icon: "store" },
    ],
  },
  // Everything below is read-only across branches; records are created and
  // changed on the branch portals.
  {
    section: "Oversight",
    items: [
      {
        panel: "sales-orders",
        title: "Sales & Orders",
        icon: "receipt",
        links: [
          { path: "/pos/website-orders", label: "Website Orders" },
          { path: "/pos/sales-return", label: "Sales Return" },
          { path: "/pos/return-requests", label: "Return Requests" },
          { path: "/pos/cash-denominations", label: "Cash Denomination" },
        ],
      },
      {
        panel: "inventory",
        title: "Inventory",
        icon: "inventory_2",
        links: [
          { path: "/pos/products", label: "Products" },
          { path: "/pos/stock-adjustment", label: "Stock Adjustment" },
          { path: "/pos/stock-transactions", label: "Stock Transaction" },
          { path: "/pos/damage-products", label: "Damage Products" },
        ],
      },
      {
        panel: "report",
        title: "Reports",
        icon: "insights",
        links: [
          { path: "/pos/customers", label: "Customers" },
          { path: "/pos/sales-report", label: "Sales Report" },
          { path: "/pos/products-report", label: "Product Report" },
          { path: "/pos/exports", label: "Exports" },
        ],
      },
    ],
  },
  {
    section: "Administration",
    items: [
      {
        panel: "hr",
        title: "HR",
        icon: "badge",
        links: [
          { path: "/pos/staff", label: "Staff" },
          { path: "/pos/attendance", label: "Attendance" },
          { path: "/pos/incentives", label: "Incentives" },
        ],
      },
      { path: "/pos/users", title: "Users", icon: "person" },
    ],
  },
]

const LeftSidebarMenu: React.FC<LeftSidebarMenu> = ({ toggleActive }) => {
  const location = useLocation()
  const [expanded, setExpanded] = React.useState<string | false>("")
  const permissions = new Set<string>(
    useAppSelector((state) => state.auth.user?.permissions ?? [])
  )
  const canView = (permission?: string) =>
    !permission || permissions.has(permission)
  const branchMenuItems = allMenuItems
    .map((section) => ({
      ...section,
      items: section.items
        .map((item) => {
          if (!("links" in item)) {
            return item
          }

          return {
            ...item,
            links: item.links.filter((link) => canView(link.permission)),
          }
        })
        .filter((item) =>
          "links" in item ? item.links.length > 0 : canView(item.permission)
        ),
    }))
    .filter((section) => section.items.length > 0)
  const menuItems = isAdminPortal() ? adminMenuItems : branchMenuItems

  // Open the dropdown that holds the current page. It is worked out on every
  // render but only applied when the page or that dropdown changes (e.g. once
  // permissions load), so opening another dropdown by hand is not undone.
  const isOnPath = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`)
  const currentPanel = menuItems
    .flatMap((section) => section.items)
    .find((item) => "links" in item && item.links.some((l) => isOnPath(l.path)))
  const currentPanelName =
    currentPanel && "links" in currentPanel ? currentPanel.panel : undefined

  React.useEffect(() => {
    if (currentPanelName) {
      setExpanded(currentPanelName)
    }
  }, [location.pathname, currentPanelName])

  const handleChange =
    (panel: string) => (_: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false)
    }

  return (
    <div className="leftSidebarDark">
      <div className="left-sidebar-menu">
        <div className="logo">
          <Link to="/pos/dashboard">
            <img
              src="/images/logo-icon.svg"
              alt="logo-icon"
              width={26}
              height={26}
            />
            <span>POS</span>
          </Link>
        </div>

        <Box className="burger-menu" onClick={toggleActive}>
          <span className="top-bar"></span>
          <span className="middle-bar"></span>
          <span className="bottom-bar"></span>
        </Box>

        <div className="sidebar-inner">
          <div className="sidebar-menu">
            {menuItems.map((section, index) => (
              <div key={index}>
                <Typography
                  className="sub-title"
                  sx={{
                    fontWeight: "500",
                    textTransform: "uppercase",
                  }}
                >
                  {section.section}
                </Typography>
                {section.items.map((item) => {
                  if ("links" in item) {
                    return (
                      <Accordion
                        key={item.panel}
                        expanded={expanded === item.panel}
                        onChange={handleChange(item.panel)}
                        className="mat-accordion"
                      >
                        <AccordionSummary
                          className="mat-summary"
                          aria-controls={`${item.panel}d-content`}
                          id={`${item.panel}d-header`}
                        >
                          <i className="material-symbols-outlined">
                            {item.icon}
                          </i>
                          <span className="title">{item.title}</span>
                          {item.badge && (
                            <span className="trezo-badge">{item.badge}</span>
                          )}
                        </AccordionSummary>

                        <AccordionDetails className="mat-details">
                          <ul className="sidebar-sub-menu">
                            {item.links.map((link) => (
                              <li key={link.path} className="sidemenu-item">
                                <Link
                                  to={link.path}
                                  className={`sidemenu-link ${isOnPath(link.path) ? "active" : ""}`}
                                >
                                  {link.label}
                                  {link.badge && (
                                    <span
                                      className={`trezo-badge ${link.badgeStyle || ""}`}
                                    >
                                      {link.badge}
                                    </span>
                                  )}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </AccordionDetails>
                      </Accordion>
                    )
                  } else {
                    // Determine if this menu item should be active
                    const isActive =
                      location.pathname === item.path ||
                      (item.path !== "/pos/dashboard" &&
                        location.pathname.startsWith(
                          item.path + "/pos/dashboard"
                        ))

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`sidebar-menu-link ${isActive ? "active" : ""}`}
                      >
                        <i className="material-symbols-outlined">{item.icon}</i>
                        <span className="title">{item.title}</span>
                      </Link>
                    )
                  }
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default LeftSidebarMenu
